import React from "react";

import { faPhoneAlt, faVideo } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { addDays, isPast } from "date-fns";

import { GlobalKeyboardListener } from "~/common";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isDesktop } from "~/lib/utils";
import { type CALL, CallIcon, callName, maybeOpenCompanionMode } from "~/stream/VideoCallButton";
import { AnchorButton, Button } from "~/ui";
import Menu from "~/ui/menu";

import type { RowProps } from "./index";

import styles from "./ViewEvent.module.css";

type Props = RowProps & {
	expanded: boolean;
};

export default function JoinConferenceRow({ event, eventData, expanded }: Props) {
	const onJoinClick = React.useCallback(
		(call: CALL) => (e: React.MouseEvent) => {
			dispatchAnalyticsTrack("View event action", { feature: "Calendar", action: "join conference" });

			const isModifierActive = ["Alt", "AltGraph", "Control", "Hyper", "Meta", "OS", "Shift", "Super"].some(key =>
				e.getModifierState(key),
			);

			if (!isDesktop || isModifierActive) return;

			e.preventDefault();

			const w = window.screen.availWidth / 2;
			const h = window.screen.availHeight / 2;

			const windowFeatures = `width=${w},height=${h}`;

			maybeOpenCompanionMode(event.guid, call);
			window.open(call.url, "_blank", windowFeatures);
		},
		[event.guid],
	);

	const joinCallShortcut = React.useCallback(
		(call: CALL) => () => {
			dispatchAnalyticsTrack("View event action (via keyboard shortcut)", {
				feature: "Calendar",
				action: "join conference",
			});

			const w = window.screen.availWidth / 2;
			const h = window.screen.availHeight / 2;

			const windowFeatures = `width=${w},height=${h}`;

			maybeOpenCompanionMode(event.guid, call);
			window.open(call.url, "_blank", windowFeatures);
		},
		[event.guid],
	);

	const [conferenceMenuOpen, setConferenceMenuOpen] = React.useState(false);
	const openConferenceMenu = React.useCallback(() => setConferenceMenuOpen(true), []);

	const calls = eventData?.conferenceCalls;
	if (!calls || calls.length === 0) {
		return null;
	}

	const oneDayPassed = isPast(addDays(event.start, 1));

	if (!expanded && oneDayPassed) {
		return null;
	}

	if (calls.length > 1 && !expanded) {
		return (
			<div className={styles.infoRow}>
				<GlobalKeyboardListener sequence="j" onEvent={openConferenceMenu} />
				<div className={cx(styles.infoRow__icon, styles.centered, styles.alignLeft)}>
					<FontAwesomeIcon icon={faVideo} />
				</div>
				<Menu open={conferenceMenuOpen} onOpenChange={setConferenceMenuOpen}>
					<Menu.Trigger>
						<Button variant="secondary" size="slim">
							Join Conference&ensp;
							<FontAwesomeIcon icon={conferenceMenuOpen ? faCaretUp : faCaretDown} />
						</Button>
					</Menu.Trigger>
					<Menu.Content>
						{calls.map(call => (
							<Menu.ItemLink
								key={call.url}
								href={call.url}
								target="_blank"
								rel="noopener noreferrer"
								onClick={onJoinClick(call)}
								className={styles.hideExternalIcon}
							>
								<span className={styles.infoRow__conferenceOption}>
									<CallIcon provider={call.provider} />
									&ensp;Join {callName(call.provider)}
								</span>
							</Menu.ItemLink>
						))}
					</Menu.Content>
				</Menu>
			</div>
		);
	}

	return (
		<>
			<GlobalKeyboardListener sequence="j" onEvent={joinCallShortcut(calls[0])} />
			{calls.map(call => (
				<React.Fragment key={call.url}>
					{call.url && (
						<div className={styles.infoRow}>
							<div className={cx(styles.infoRow__icon, styles.centered, styles.alignLeft)}>
								<CallIcon provider={call.provider} />
							</div>
							<AnchorButton
								size="slim"
								variant="secondary"
								href={call.url}
								target="_blank"
								rel="noopener noreferrer"
								onClick={onJoinClick(call)}
							>
								<span>Join {callName(call.provider)}</span>
							</AnchorButton>
						</div>
					)}

					{expanded && call.phoneNumber && (
						<div className={styles.infoRow} data-test-id="meeting-info-modal-phone">
							<div className={cx(styles.infoRow__icon, styles.centeredOnFirstLine)}>
								<FontAwesomeIcon fixedWidth icon={faPhoneAlt} />
							</div>
							<div className={cx(styles.infoRow__text, styles.textSmall)}>
								<div>{call.phoneNumber}</div>
								{call.pin && <div className={styles.secondaryText}>PIN: {call.pin}#</div>}
							</div>
						</div>
					)}
				</React.Fragment>
			))}
		</>
	);
}
